'use client';

import { useMemo, useEffect, useState } from 'react';
import { useCalendarStore, hydrateThemeStyle, hydrateCalendarLayout, hydrateCalendarSystem } from '@/stores/calendar-store';
import { getEventsForLens, getEventsForCountry, getNavConfig } from '@/lib/data-source';
import { getDefaultCalendarSystem, getCalendarAdapter, type CalendarSystem } from '@/lib/calendar-systems';
import type { CalendarEvent, LensId, EntityOption, CountryCode } from '@/lib/types';
import type { TickerItem } from '@/lib/event-filters';
import { getAlternateLanguages, getAlternateUniversityLanguages, getAlternateCityLanguages, getAlternateCountryLensLanguages, getAlternateEvergreenLanguages, uiFb } from '@/lib/i18n';
import { getLangLabel, getLangLabelEn } from '@/lib/i18n-helpers';
import { useAutoLang } from '@/hooks/useAutoLang';
import { useAutoCountry } from '@/hooks/useAutoCountry';
import { buildLongWeekendHighlights, getLongWeekends } from '@/lib/long-weekends';
import type { LongWeekend } from '@/lib/long-weekends';
import { getFilterConfigForPage } from '@/lib/filter-configs';

interface UseCalendarViewLogicProps {
  lens: LensId;
  entity: string;
  entities: Record<LensId, EntityOption[]>;
  lang: string;
  mode: 'landing' | 'content';
  countryCode?: string;
  events?: CalendarEvent[];
  initialFilter?: string;
}

export function useCalendarViewLogic({
  lens,
  entity,
  entities,
  lang,
  mode,
  countryCode: countryCodeProp,
  events: eventsProp,
  initialFilter,
}: UseCalendarViewLogicProps) {
  const { setActiveLens, setLang, activeLens: storeLens, activeEntity: storeEntity, year, month, calendarSystem } = useCalendarStore();

  useEffect(() => {
    setLang(lang);
  }, [lang, setLang]);

  // Country code: prefer explicit prop, fall back to entity for country lens
  const countryCode = countryCodeProp ?? (lens === 'country' ? entity : undefined);

  // Page-default calendar system, used as fallback when user has no localStorage preference.
  // E.g. on the Saudi Arabia page, default to islamic-umalqura even if browser is en-US.
  const pageDefaultCalendarSystem: CalendarSystem = useMemo(
    () => getDefaultCalendarSystem(lang, countryCode),
    [lang, countryCode]
  );

  // Hydrate theme + layout + calendar system from localStorage after mount to avoid SSR mismatch
  useEffect(() => {
    hydrateThemeStyle();
    // Academic pages have ~20 milestone dates spread across the year — a month
    // grid shows mostly-empty months, so default new visitors to the timeline.
    hydrateCalendarLayout(lens === 'academic' ? 'timeline' : undefined);
    hydrateCalendarSystem(pageDefaultCalendarSystem);
  }, [pageDefaultCalendarSystem, lens]);

  // Adapter for the active calendar system. Stable per-render via the registry.
  const calendarAdapter = useMemo(() => getCalendarAdapter(calendarSystem), [calendarSystem]);

  // The grid's anchor day in Gregorian (the 1st of the active calendar's month that contains
  // the Gregorian {year, month}). Used by FreeCalendarView/CalendarGrid in bead 6.
  const anchorGregorian = useMemo(() => {
    // store's `month` is 0-indexed (JS Date convention); store's `year` is Gregorian.
    const gregorianAnchor = new Date(Date.UTC(year, month, 1));
    if (calendarSystem === 'gregorian') return gregorianAnchor;
    // Find the active-calendar-month that contains gregorianAnchor → return its day-1 in Gregorian.
    const parts = calendarAdapter.fromGregorian(gregorianAnchor);
    return calendarAdapter.toGregorian(parts.year, parts.month, 1);
  }, [year, month, calendarSystem, calendarAdapter]);

  // All events for this lens (all years) — used to derive available years
  const allYearEvents: CalendarEvent[] = useMemo(
    () => eventsProp ?? getEventsForLens(lens, entity),
    [lens, entity, eventsProp]
  );

  // Available years for the year selector
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    for (const e of allYearEvents) {
      years.add(e.year);
    }
    return [...years].sort();
  }, [allYearEvents]);

  // Events filtered to the selected year. Academic pages are exempt: their
  // year is an *academic* year spanning two calendar years (e.g. 2026/2027
  // events fall in both 2026 and 2027), so calendar-year filtering would hide
  // the second half. The events prop is already scoped to the academic year.
  const allEvents: CalendarEvent[] = useMemo(
    () => (lens === 'academic' ? allYearEvents : allYearEvents.filter(e => e.year === year)),
    [allYearEvents, year, lens]
  );

  // Active filter state (replaces old per-lens typeFilter)
  const [activeFilter, setActiveFilter] = useState(initialFilter ?? 'all');

  // When long-weekend or nature lens is active in store, filter events via getEventsForLens
  const events = useMemo(() => {
    if (storeLens === 'long-weekend' && storeEntity) {
      const raw = getEventsForLens('long-weekend', storeEntity);
      return raw.filter(e => e.year === year);
    }
    if (storeLens === 'nature' && storeEntity) {
      const raw = getEventsForLens('nature', storeEntity);
      return raw.filter(e => e.year === year);
    }
    return allEvents;
  }, [allEvents, storeLens, storeEntity, year]);

  // Unified filter config — replaces old per-lens if-chains
  const filterConfig = useMemo(
    () => getFilterConfigForPage(lens, lang, countryCode, entities, year, entity),
    [lens, lang, countryCode, entities, year, entity]
  );

  // Apply filter predicate to events
  const filteredEvents = useMemo(() => {
    if (!filterConfig || activeFilter === 'all') return events;
    return events.filter(filterConfig.predicate(activeFilter));
  }, [events, activeFilter, filterConfig]);

  // Parse subType from storeEntity (e.g. "ID:natural" → "natural")
  const lwSubType = useMemo(() => {
    if (storeLens === 'long-weekend' && storeEntity) {
      const parts = storeEntity.split(':');
      return (parts[1] as 'natural' | 'all') || 'all';
    }
    return undefined;
  }, [storeLens, storeEntity]);

  // Compute long weekend highlight map when lens is active
  const longWeekendHighlights = useMemo(() => {
    if (storeLens === 'long-weekend' && countryCode) {
      return buildLongWeekendHighlights(
        getEventsForCountry(countryCode as CountryCode),
        lwSubType
      );
    }
    return null;
  }, [storeLens, countryCode, lwSubType]);

  // Compute long weekends array for vacation cards
  const longWeekends: LongWeekend[] | null = useMemo(() => {
    if (storeLens === 'long-weekend' && countryCode) {
      let lws = getLongWeekends(getEventsForCountry(countryCode as CountryCode), countryCode as CountryCode);
      if (lwSubType === 'natural') {
        lws = lws.filter(lw => lw.leaveDaysNeeded === 0);
      }
      return lws;
    }
    return null;
  }, [storeLens, countryCode, lwSubType]);

  const activeOption = entities[lens]?.find((e) => e.id === entity || e.id === entity.split(':')[0]);
  // When curated events are explicitly passed in landing mode, use a generic label
  const isCuratedLanding = mode === 'landing' && !!eventsProp;
  const entityLabel = isCuratedLanding ? 'World' : (activeOption?.label || entity);
  const entityIcon = isCuratedLanding ? '🌍' : (activeOption?.icon || '');

  const langAlternates = useMemo(() => {
    try {
      if (lens === 'country') {
        return getAlternateLanguages(entity as CountryCode);
      }
      if (lens === 'academic') {
        const [uniId, yearStr] = entity.split(':');
        return getAlternateUniversityLanguages(uniId, yearStr ? parseInt(yearStr, 10) : undefined);
      }
      if (lens === 'city') {
        return getAlternateCityLanguages(entity);
      }
      if (lens === 'religion' || lens === 'nature') {
        const code = countryCodeProp ?? entity.split(':')[0];
        if (code && code.length === 2) {
          return getAlternateCountryLensLanguages(code as CountryCode, lens);
        }
      }
      // Evergreen lenses (long-weekend, best-time, etc.)
      const evergreenLenses = ['long-weekend', 'best-time', 'country-month', 'festival-index', 'phenomenon', 'admission', 'cross-calendar', 'university-admission'];
      if (evergreenLenses.includes(lens)) {
        const code = countryCodeProp ?? entity.split(':')[0];
        if (code) {
          const evergreenMap = getAlternateEvergreenLanguages(`${lens}:${code}`);
          return Object.entries(evergreenMap).map(([lang, slug]) => ({
            lang,
            slug,
            label: getLangLabel(lang),
            labelEn: getLangLabelEn(lang),
          }));
        }
      }
    } catch {
      // Fall through to empty array
    }
    return [];
  }, [lens, entity, countryCodeProp]);

  useAutoLang(langAlternates, lang);

  // Redirect homepage visitors to their country page
  const isHomepage = mode === 'landing' && !entity;
  useAutoCountry(isHomepage);

  const previewSlug = langAlternates.find(a => a.lang === lang)?.slug;

  const contentNavLinks = mode === 'content' ? [
    { href: '#calendar', label: uiFb('ui.calendar', 'Calendar', lang) },
    { href: '/interactive', label: uiFb('ui.interactive', 'Interactive', lang) },
  ] : undefined;

  const pageType = mode === 'landing' ? 'homepage' : lens;
  const navConfig = getNavConfig(pageType);

  return {
    year,
    events,
    filteredEvents,
    activeFilter,
    setActiveFilter,
    filterConfig,
    availableYears,
    longWeekendHighlights,
    longWeekends,
    entityLabel,
    entityIcon,
    langAlternates,
    previewSlug,
    contentNavLinks,
    navConfig,
    countryCode,
    // Multi-calendar (PRD: prd-multi-calendar-toggle.md)
    calendarSystem,
    calendarAdapter,
    pageDefaultCalendarSystem,
    anchorGregorian,
  };
}
